import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import { BsDownload } from "react-icons/bs";
import { toast } from "react-toastify";
import { TextField, MenuItem, Dialog } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Loader from "../components/Loader";
import { PaginatedItems } from "../components/Paginated";
import { getSeshaasaiJobsReport } from "../Path/Path";

const HEADERS = [
  "Created Time",
  "Job ID",
  "PO Number",
  "Vendor",
  "Status",
  "SKU Count",
  "EPC Count",
  "EPCs",
];
const COL_NAMES = [
  "createdTime",
  "jobId",
  "poNumber",
  "vendor",
  "statusLabel",
  "skuCount",
  "epcCount",
  "epcsAction",
];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "Pending", label: "Pending" },
  { value: "In-Progress", label: "In-Progress" },
  { value: "Printed", label: "Completed" },
];

const statusLabel = (status) => {
  if (status === "Printed") return "Completed";
  return status || "--";
};

const statusColor = (status) => {
  if (status === "Pending") return "#ffd43b";
  if (status === "In-Progress") return "#74c0fc";
  if (status === "Printed") return "#51cf66";
  return "#ddd";
};

const formatDubai = (value) => {
  if (!value) return "--";
  const date = moment(value).tz("Asia/Dubai");
  return date.isValid() ? date.format("MM/DD/YYYY hh:mm:ss A") : "--";
};

const SeshaasaiJobsReport = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState("");
  const [jobId, setJobId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [rows, setRows] = useState([]);
  const [paginationData, setPaginationData] = useState({ next: 0, previous: 0 });
  const [counts, setCounts] = useState({ pending: 0, inProgress: 0, printed: 0 });
  const [loading, setLoading] = useState(false);
  const [epcJob, setEpcJob] = useState(null);

  useEffect(() => {
    getReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const datesAreValid = (from, to) => {
    if (from && !moment(from).isValid()) return false;
    if (to && !moment(to).isValid()) return false;
    if (from && moment(from).isAfter(moment(), "day")) return false;
    if (to && moment(to).isAfter(moment(), "day")) return false;
    if (from && to && moment(from).isAfter(moment(to), "day")) return false;
    return true;
  };

  const filtersFromState = (overrides) => ({
    fromDate,
    toDate,
    status,
    jobId,
    poNumber,
    ...(overrides || {}),
  });

  const queryFromFilters = (filters, extra = {}) => ({
    jobId: String(filters.jobId || "").trim() || undefined,
    poNumber: String(filters.poNumber || "").trim() || undefined,
    status: filters.status || undefined,
    startDate: filters.fromDate
      ? moment(filters.fromDate).format("YYYY-MM-DD")
      : undefined,
    endDate: filters.toDate
      ? moment(filters.toDate).format("YYYY-MM-DD")
      : undefined,
    ...extra,
  });

  const getReport = async (pageNumber = 1, overrides) => {
    const filters = filtersFromState(overrides);
    if (!datesAreValid(filters.fromDate, filters.toDate)) {
      toast.error("Please enter a valid date range.");
      return;
    }

    setLoading(true);
    try {
      const res = await getSeshaasaiJobsReport(
        queryFromFilters(filters, { page: pageNumber })
      );
      const payload = res?.data;
      const list = payload?.data;
      if (Array.isArray(list)) {
        setRows(list);
        setPaginationData(payload);
        setCounts(payload?.counts || { pending: 0, inProgress: 0, printed: 0 });
        if (list.length === 0) toast.error("No data found.");
      } else {
        setRows([]);
        toast.error(res?.error?.message || "Failed to fetch report.");
      }
    } catch (err) {
      console.error("Error fetching Seshaasai jobs report:", err);
      const msg =
        err?.response?.data?.error?.message || "Failed to fetch report.";
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    const filters = filtersFromState();
    if (!datesAreValid(filters.fromDate, filters.toDate)) {
      toast.error("Please enter a valid date range.");
      return;
    }

    setLoading(true);
    try {
      const res = await getSeshaasaiJobsReport(
        queryFromFilters(filters, { size: "all" })
      );
      const list = res?.data?.data || [];
      if (!Array.isArray(list) || list.length === 0) {
        toast.error("Nothing to export.");
        return;
      }

      const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const csvHeaders = [
        "Created Time",
        "Job ID",
        "PO Number",
        "Vendor",
        "Status",
        "SKU Count",
        "EPC Count",
        "SKU",
        "Qty",
        "Size",
        "EPC",
      ];
      const csvRows = [];
      list.forEach((job) => {
        const printData =
          Array.isArray(job?.printData) && job.printData.length
            ? job.printData
            : [{ SKU: "", Qty: "", Size: "", EPCs: [] }];
        printData.forEach((item) => {
          const epcs = item?.EPCs?.length ? item.EPCs : [""];
          epcs.forEach((epc) => {
            csvRows.push(
              [
                escape(formatDubai(job?.createdAt) === "--" ? "" : formatDubai(job?.createdAt)),
                escape(job?.jobId || ""),
                escape(job?.poNumber ? `\t${job.poNumber}` : ""),
                escape(job?.vendorDescription || job?.vendorCode || ""),
                escape(statusLabel(job?.status) === "--" ? "" : statusLabel(job?.status)),
                Number(job?.skuCount) || 0,
                Number(job?.epcCount) || 0,
                escape(item?.SKU ? `\t${item.SKU}` : ""),
                item?.Qty ?? "",
                escape(item?.Size || ""),
                escape(epc ? `\t${epc}` : ""),
              ].join(",")
            );
          });
        });
      });

      const csv = [csvHeaders.map(escape).join(","), ...csvRows].join("\n");
      const url = window.URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8;" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Seshaasai_Jobs.csv");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting Seshaasai jobs report:", err);
      toast.error(
        err?.response?.data?.error?.message || "Failed to export CSV."
      );
    } finally {
      setLoading(false);
    }
  };

  const applyStatus = (nextStatus) => {
    setStatus(nextStatus);
    getReport(1, { status: nextStatus });
  };

  const clearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setStatus("");
    setJobId("");
    setPoNumber("");
    getReport(1, {
      fromDate: null,
      toDate: null,
      status: "",
      jobId: "",
      poNumber: "",
    });
  };

  const tableData = rows.map((item) => ({
    createdTime: formatDubai(item?.createdAt),
    jobId: item?.jobId || "--",
    poNumber: item?.poNumber || "--",
    vendor: item?.vendorDescription || item?.vendorCode || "--",
    statusLabel: (
      <span style={{ color: statusColor(item?.status), fontWeight: 600 }}>
        {statusLabel(item?.status)}
      </span>
    ),
    skuCount: Number(item?.skuCount) || 0,
    epcCount: Number(item?.epcCount) || 0,
    epcsAction: (
      <button
        type="button"
        className="run-btn"
        style={{ minWidth: "110px", padding: "4px 10px" }}
        onClick={() => setEpcJob(item)}
      >
        <p className="run-text">View EPCs</p>
      </button>
    ),
  }));

  const pageCount =
    (Number(paginationData?.next) || 0) +
    (Number(paginationData?.previous) || 0) +
    1;

  return (
    <React.Fragment>
      <Loader loading={loading} />
      <EpcModal job={epcJob} onClose={() => setEpcJob(null)} />
      <div className="dashboard-main">
        <div
          className="dashboard-top"
          style={{ display: "flex", flexDirection: "column", padding: "10px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="stock-count" style={{ fontSize: "25px", margin: 0 }}>
              Seshaasai Jobs
            </p>
            <div
              onClick={exportCsv}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <p style={{ color: "black", fontSize: "16px", margin: 0 }}>
                Export CSV
              </p>
              <BsDownload color="black" size={16} />
            </div>
          </div>
          <h1 style={{ color: "black", fontSize: "15px", marginLeft: "5px" }}>
            {new Date().toLocaleString("hi-IN")}
          </h1>
        </div>

        <div className="dashboard-bottom" style={{ marginTop: "0.5%" }}>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "14px",
            }}
          >
            <CountCard
              label="Pending"
              value={counts.pending}
              color="#ffd43b"
              active={status === "Pending"}
              onClick={() => applyStatus(status === "Pending" ? "" : "Pending")}
            />
            <CountCard
              label="In-Progress"
              value={counts.inProgress}
              color="#74c0fc"
              active={status === "In-Progress"}
              onClick={() =>
                applyStatus(status === "In-Progress" ? "" : "In-Progress")
              }
            />
            <CountCard
              label="Completed"
              value={counts.printed}
              color="#51cf66"
              active={status === "Printed"}
              onClick={() => applyStatus(status === "Printed" ? "" : "Printed")}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "10px",
            }}
          >
            <DesktopDatePicker
              label="From Date"
              inputFormat="MM/DD/YYYY"
              maxDate={toDate || new Date()}
              value={fromDate}
              onChange={(data) => setFromDate(data)}
              renderInput={(params) => (
                <TextField sx={{ width: 170 }} size="small" {...params} />
              )}
            />
            <DesktopDatePicker
              label="To Date"
              inputFormat="MM/DD/YYYY"
              minDate={fromDate || undefined}
              maxDate={new Date()}
              value={toDate}
              onChange={(data) => setToDate(data)}
              renderInput={(params) => (
                <TextField sx={{ width: 170 }} size="small" {...params} />
              )}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ width: 170 }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Job ID"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && getReport(1)}
              sx={{ width: 200 }}
            />
            <TextField
              size="small"
              label="PO Number"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && getReport(1)}
              sx={{ width: 180 }}
            />
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <button type="button" onClick={() => getReport(1)} className="run-btn">
                <p className="run-text">Search</p>
              </button>
              <button type="button" onClick={clearFilters} className="view-btn">
                <p className="run-text">Clear</p>
              </button>
            </div>
          </div>

          <div style={{ marginTop: "10px", overflowX: "auto", height: "600px" }}>
            <PaginatedItems
              pageCountNew={pageCount}
              getData={(pageNumber) => getReport(pageNumber)}
              totalLength={paginationData?.next}
              itemsPerPage={10}
              headers={HEADERS}
              colNames={COL_NAMES}
              forcePage={paginationData?.previous || 0}
              data={tableData}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
              color: "white",
              padding: "10px",
            }}
          >
            <h5>ROWS PER PAGE: 10</h5>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const CountCard = ({ label, value, color, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minWidth: "160px",
      padding: "12px 16px",
      borderRadius: "8px",
      border: active ? `2px solid ${color}` : "1px solid #555",
      background: "#222",
      color: "#fff",
      textAlign: "left",
      cursor: "pointer",
    }}
  >
    <div style={{ fontSize: "13px", color }}>{label}</div>
    <div style={{ fontSize: "26px", fontWeight: 700, lineHeight: 1.2 }}>
      {Number(value) || 0}
    </div>
  </button>
);

const EpcModal = ({ job, onClose }) => {
  const printData = Array.isArray(job?.printData) ? job.printData : [];
  return (
    <Dialog
      open={!!job}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { background: "#1e2428", color: "#fff" } }}
    >
      <div style={{ background: "#1e2428", color: "#fff", padding: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 6px" }}>EPCs — {job?.jobId || ""}</h3>
            <div style={{ fontSize: "14px", color: "#ccc" }}>
              PO {job?.poNumber || "--"} · {statusLabel(job?.status)} ·{" "}
              {Number(job?.epcCount) || 0} EPCs
            </div>
          </div>
          <button type="button" onClick={onClose} className="view-btn">
            <p className="run-text">Close</p>
          </button>
        </div>

        <div style={{ marginTop: "16px", maxHeight: "65vh", overflowY: "auto" }}>
          {printData.length === 0 ? (
            <p>No EPCs on this job.</p>
          ) : (
            printData.map((item, index) => (
              <div
                key={`${item?.SKU || "sku"}-${index}`}
                style={{
                  border: "1px solid #444",
                  borderRadius: "6px",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                  <strong>SKU:</strong> {item?.SKU || "--"} · <strong>Qty:</strong>{" "}
                  {item?.Qty ?? "--"} · <strong>Size:</strong> {item?.Size || "--"} ·{" "}
                  <strong>EPCs:</strong> {(item?.EPCs || []).length}
                </div>
                {(item?.EPCs || []).length === 0 ? (
                  <div style={{ color: "#aaa" }}>No EPCs</div>
                ) : (
                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: "13px",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {(item.EPCs || []).join("\n")}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default SeshaasaiJobsReport;
